'use client';

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useUsage } from '@/contexts/UsageContext';
import { Check } from 'lucide-react';
import { BILLING_PLANS, formatUsd, planAmount, planName, type BillingInterval } from '@/lib/billing-plans';
import { SubscriptionButton } from './SubscriptionButton';

interface BillingStatus {
  hasSubscription: boolean;
  canManage: boolean;
  amount?: number;
  currency?: string;
  interval?: string;
  status?: string;
  tier?: 'PRO' | 'ENTERPRISE';
  cancelAtPeriodEnd?: boolean;
  periodEnd?: number;
}

export function PlanPicker({ surface = 'pricing' }: { surface?: string }) {
  const { status } = useSession();
  const { currentTier, isTrialActive, messagesRemaining, checkAndUpdateLimits } = useUsage();
  const [interval, setInterval] = useState<BillingInterval>('month');
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(status !== 'unauthenticated');
  const [error, setError] = useState('');
  const [managing, setManaging] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (status === 'loading') return;
    if (status === 'unauthenticated') {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch('/api/stripe/billing', { signal: controller.signal })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error);
        setBilling(data);
      })
      .catch(e => {
        if (e.name !== 'AbortError') setError('We could not verify your billing. Retry before changing plans.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [status, reload]);

  async function manageBilling(tier?: 'PRO' | 'ENTERPRISE') {
    setManaging(true);
    setError('');
    try {
      const response = await fetch('/api/stripe/billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl: window.location.href, ...(tier ? { tier, interval } : {}) }),
      });
      const data = await response.json();
      if (!response.ok || !data.url) throw new Error(data.error || 'Billing is unavailable.');
      window.location.assign(data.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Billing is unavailable.');
    } finally {
      setManaging(false);
    }
  }
  const subscribed = billing?.hasSubscription === true;
  const billedTier = subscribed && billing?.tier ? billing.tier : currentTier;
  const needsRefresh = subscribed && billedTier !== currentTier;
  async function refreshAccess() {
    setManaging(true);
    setError('');
    try {
      const response = await fetch('/api/stripe/billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'refresh_access' }),
      });
      const result = await response.json();
      if (!response.ok || !result.active)
        throw new Error(result.error || 'Access is not confirmed yet. Please contact support.');
      await checkAndUpdateLimits();
      setReload(n => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Access could not be refreshed.');
    } finally {
      setManaging(false);
    }
  }
  const safeToBuy = status === 'unauthenticated' || (!loading && billing !== null && !error);
  return (
    <div className="billing-picker">
      <div className="billing-cycle" role="group" aria-label="Billing cycle">
        <button type="button" aria-pressed={interval === 'year'} onClick={() => setInterval('year')}>
          Annual <span>−20%</span>
        </button>
        <button type="button" aria-pressed={interval === 'month'} onClick={() => setInterval('month')}>
          Monthly
        </button>
      </div>
      <p className="billing-cycle-note">
        {interval === 'year' ? 'Save 20%, billed once a year' : 'Flexible monthly billing'}
      </p>
      <div className="billing-plans">
        {(['HOBBY', 'PRO', 'ENTERPRISE'] as const).map(tier => {
          const plan = BILLING_PLANS[tier];
          const isFree = tier === 'HOBBY';
          const amount = isFree ? 0 : planAmount(tier, interval);
          const effective = interval === 'year' ? amount / 12 : amount;
          const current = tier === billedTier && (!isTrialActive || subscribed);
          return (
            <section
              key={tier}
              className={`billing-plan ${tier === 'PRO' ? 'billing-plan-recommended' : ''}`}
              aria-label={`${plan.name} plan`}>
              <div className="billing-plan-name">
                <h3>{plan.name}</h3>
                {tier === 'PRO' && <span>Recommended</span>}
              </div>
              <p className="billing-plan-price">
                <strong>{isFree ? 'Free' : formatUsd(effective)}</strong>
                <span>{isFree ? 'for everyone' : '/ month'}</span>
              </p>
              <p className="billing-plan-charge">
                {isFree
                  ? 'No card needed'
                  : interval === 'year'
                    ? `${formatUsd(amount)} USD billed yearly`
                    : `${formatUsd(amount)} USD billed monthly`}
              </p>
              {isFree ? (
                <button
                  className="billing-plan-button"
                  type="button"
                  disabled={status !== 'unauthenticated'}
                  onClick={() => window.location.assign('/auth/signin')}>
                  {status === 'unauthenticated' ? 'Start free' : current ? 'Current plan' : 'Included'}
                </button>
              ) : subscribed ? (
                <button
                  className="billing-plan-button"
                  type="button"
                  onClick={() => manageBilling(current && interval === billing?.interval ? undefined : tier)}
                  disabled={managing || !billing?.canManage}>
                  {managing
                    ? 'Opening billing…'
                    : current
                      ? interval === billing?.interval
                        ? 'Manage plan'
                        : 'Change billing cycle'
                      : `Change to ${plan.name}`}
                </button>
              ) : (
                <SubscriptionButton
                  tier={tier}
                  interval={interval}
                  checkoutSource={surface}
                  label={`Select ${plan.name}`}
                  disabled={!safeToBuy}
                  className="billing-plan-button"
                />
              )}
              <p className="billing-plan-includes">{isFree ? 'What’s included:' : 'More room to create:'}</p>
              <ul>
                {plan.features.map(feature => (
                  <li key={feature}>
                    <Check aria-hidden="true" size={14} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
      <div className="billing-footnote">
        <p>
          Styling and supported clean PNG downloads stay free. Prices are per account, in USD. Review your final total
          before payment.
        </p>
        {loading ? (
          <p role="status">Checking your current billing…</p>
        ) : subscribed ? (
          <p>
            Your current {planName(billedTier)} subscription:{' '}
            {typeof billing.amount === 'number'
              ? new Intl.NumberFormat('en-US', { style: 'currency', currency: billing.currency || 'usd' }).format(
                  billing.amount / 100
                )
              : 'see billing'}{' '}
            / {billing.interval}.{' '}
            {billing.cancelAtPeriodEnd
              ? 'Cancels at the end of its paid period.'
              : 'Existing pricing stays unchanged unless you choose a change.'}
          </p>
        ) : isTrialActive ? (
          <p>Your trial access is unchanged. Paid plans renew automatically until canceled.</p>
        ) : (
          <p>
            Paid plans renew automatically. Cancel through billing at any time.{' '}
            {status === 'authenticated' && messagesRemaining >= 0
              ? `${messagesRemaining} AI generation${messagesRemaining === 1 ? '' : 's'} available now.`
              : ''}
          </p>
        )}
        {needsRefresh && (
          <p>
            Your billing and app access differ.{' '}
            <button className="billing-text-button" onClick={refreshAccess} disabled={managing}>
              Refresh paid access
            </button>
          </p>
        )}
        {billing?.canManage && !subscribed && (
          <button className="billing-text-button" onClick={() => manageBilling()} disabled={managing}>
            Manage previous billing
          </button>
        )}
        {error && (
          <p role="alert" className="billing-error">
            {error} <button onClick={() => setReload(n => n + 1)}>Retry</button>
          </p>
        )}
      </div>
    </div>
  );
}
